import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Typography,
  Stack,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import _ from 'lodash';
import superadminServices from 'app/services/superadmin-services';
import {
  ActionIconButton,
  CreateButton,
  ResponsiveDataTable,
  SaveButton,
  StandardPageHeader,
} from 'app/shared/ui';

// Vision: los Jetson de Xpit AI Vision (llegan por AWS IoT, no por radio).
const INTERFACES = ['Zigbee', 'LoRa', 'Bluetooth', 'Wi-Fi', 'Z-Wave', 'Vision'];
const CATEGORIES = ['climate', 'motion', 'energy', 'water', 'air_quality', 'agriculture', 'location', 'security', 'vision'];

const EMPTY_MEASUREMENT = { id: null, name: '', position: '' };
const EMPTY_DEVICE_TYPE = {
  id: null,
  brand: '',
  model: '',
  interface: '',
  category: '',
  subcategory: '',
  position: '',
  measurement_type_ids: [],
};

const DeviceTypesIndex = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);

  const [openMeasurementForm, setOpenMeasurementForm] = useState(false);
  const [measurementForm, setMeasurementForm] = useState(EMPTY_MEASUREMENT);

  const [openDeviceTypeForm, setOpenDeviceTypeForm] = useState(false);
  const [deviceTypeForm, setDeviceTypeForm] = useState(EMPTY_DEVICE_TYPE);

  const measurementOptions = useMemo(() => measurementTypes.map((item) => ({ id: item.id, name: item.name })), [measurementTypes]);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      superadminServices.getMeasurementTypes('?page=1'),
      superadminServices.getDeviceTypes('?page=1&include=measurement_types'),
    ])
      .then(([measurementResp, deviceTypeResp]) => {
        setMeasurementTypes(measurementResp?.data || []);
        setDeviceTypes(deviceTypeResp?.data || []);
      })
      .catch(() => setNotice({ severity: 'error', text: 'No se pudo cargar catálogo.' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveMeasurement = () => {
    if (!measurementForm.name?.trim()) return;

    setSaving(true);
    const body = {
      measurement_type: {
        name: measurementForm.name,
        position: measurementForm.position || null,
      },
    };

    const request = measurementForm.id
      ? superadminServices.updateMeasurementType(measurementForm.id, body)
      : superadminServices.createMeasurementType(body);

    request
      .then(() => {
        setOpenMeasurementForm(false);
        setNotice({ severity: 'success', text: 'Tipo de medición guardado.' });
        loadData();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo guardar el tipo de medición.';
        setNotice({ severity: 'error', text: msg });
      })
      .finally(() => setSaving(false));
  };

  const saveDeviceType = () => {
    if (!deviceTypeForm.brand?.trim() || !deviceTypeForm.model?.trim() || !deviceTypeForm.interface || !deviceTypeForm.category) {
      setNotice({ severity: 'error', text: 'Completa marca, modelo, interfaz y categoría.' });
      return;
    }

    setSaving(true);
    const body = {
      device_type: {
        brand: deviceTypeForm.brand,
        model: deviceTypeForm.model,
        interface: deviceTypeForm.interface,
        category: deviceTypeForm.category,
        subcategory: deviceTypeForm.subcategory,
        position: deviceTypeForm.position || null,
        measurement_type_ids: deviceTypeForm.measurement_type_ids,
      },
    };

    const request = deviceTypeForm.id
      ? superadminServices.updateDeviceType(deviceTypeForm.id, body)
      : superadminServices.createDeviceType(body);

    request
      .then(() => {
        setOpenDeviceTypeForm(false);
        setNotice({ severity: 'success', text: 'Tipo de dispositivo guardado.' });
        loadData();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo guardar el tipo de dispositivo.';
        setNotice({ severity: 'error', text: msg });
      })
      .finally(() => setSaving(false));
  };

  const destroyMeasurement = (record) => {
    if (!window.confirm(`¿Eliminar tipo de medición ${record.name}?`)) return;

    superadminServices.deleteMeasurementType(record.id)
      .then(() => {
        setNotice({ severity: 'success', text: 'Tipo de medición eliminado.' });
        loadData();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo eliminar el tipo de medición.';
        setNotice({ severity: 'error', text: msg });
      });
  };

  const destroyDeviceType = (record) => {
    if (!window.confirm(`¿Eliminar tipo de dispositivo ${record.brand} ${record.model}?`)) return;

    superadminServices.deleteDeviceType(record.id)
      .then(() => {
        setNotice({ severity: 'success', text: 'Tipo de dispositivo eliminado.' });
        loadData();
      })
      .catch((error) => {
        const msg = error?.response?.data?.errors?.join(', ') || 'No se pudo eliminar el tipo de dispositivo.';
        setNotice({ severity: 'error', text: msg });
      });
  };

  const renderMeasurementActions = (record) => (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
      <ActionIconButton
        size="small"
        title="Editar measurement"
        onClick={() => {
          setMeasurementForm({ id: record.id, name: record.name || '', position: record.position || '' });
          setOpenMeasurementForm(true);
        }}>
        <EditOutlinedIcon fontSize="small" />
      </ActionIconButton>
      <ActionIconButton
        size="small"
        title="Eliminar measurement"
        onClick={() => destroyMeasurement(record)}>
        <DeleteForeverOutlinedIcon fontSize="small" color="error" />
      </ActionIconButton>
    </Stack>
  );

  const measurementColumns = [
    { key: 'name', label: 'Nombre', render: (record) => record.name || '-' },
    { key: 'position', label: 'Pos.', render: (record) => record.position ?? '-' },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      mobileHidden: true,
      render: (record) => renderMeasurementActions(record),
    },
  ];

  const renderDeviceTypeActions = (record) => (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
      <ActionIconButton
        size="small"
        title="Editar device type"
        onClick={() => {
          setDeviceTypeForm({
            id: record.id,
            brand: record.brand || '',
            model: record.model || '',
            interface: record.interface || '',
            category: record.category || '',
            subcategory: record.subcategory || '',
            position: record.position || '',
            measurement_type_ids: _.map(record.measurement_types || [], 'id'),
          });
          setOpenDeviceTypeForm(true);
        }}>
        <EditOutlinedIcon fontSize="small" />
      </ActionIconButton>
      <ActionIconButton
        size="small"
        title="Eliminar device type"
        onClick={() => destroyDeviceType(record)}>
        <DeleteForeverOutlinedIcon fontSize="small" color="error" />
      </ActionIconButton>
    </Stack>
  );

  const deviceTypeColumns = [
    { key: 'brand_model', label: 'Marca / Modelo', render: (record) => _.compact([record.brand, record.model]).join(' ') || '-' },
    { key: 'interface', label: 'Interfaz', render: (record) => record.interface || '-' },
    { key: 'category', label: 'Categoría', render: (record) => record.category || '-' },
    { key: 'subcategory', label: 'Subcategoría', render: (record) => record.subcategory || '-' },
    { key: 'measurements', label: 'Measurements', render: (record) => _.map(record.measurement_types || [], (m) => m.name).join(', ') || '-' },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      mobileHidden: true,
      render: (record) => renderDeviceTypeActions(record),
    },
  ];

  return (
    <Box>
      <StandardPageHeader
        title="Superadministración · DeviceTypes y MeasurementTypes"
        actions={(
          <>
            <CreateButton onClick={() => { setMeasurementForm(EMPTY_MEASUREMENT); setOpenMeasurementForm(true); }}>
              Nuevo measurement
            </CreateButton>
            <CreateButton onClick={() => { setDeviceTypeForm(EMPTY_DEVICE_TYPE); setOpenDeviceTypeForm(true); }}>
              Nuevo device type
            </CreateButton>
          </>
        )}
      />

      {!!notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h5" sx={{ mb: 1.5 }}>Measurement Types</Typography>
              <ResponsiveDataTable
                columns={measurementColumns}
                rows={measurementTypes}
                rowKey="id"
                emptyMessage="No hay measurements para mostrar."
                mobileTitle={(record) => record.name || '-'}
                mobileSubtitle={(record) => `Posición: ${record.position ?? '-'}`}
                renderRowActions={(record) => renderMeasurementActions(record)}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h5" sx={{ mb: 1.5 }}>Device Types</Typography>
              <ResponsiveDataTable
                columns={deviceTypeColumns}
                rows={deviceTypes}
                rowKey="id"
                emptyMessage="No hay device types para mostrar."
                mobileTitle={(record) => _.compact([record.brand, record.model]).join(' ') || '-'}
                mobileSubtitle={(record) => `${record.interface || '-'} · ${record.category || '-'}`}
                renderRowActions={(record) => renderDeviceTypeActions(record)}
              />
            </Paper>
          </Grid>
        </Grid>
      )}

      <Dialog open={openMeasurementForm} onClose={() => setOpenMeasurementForm(false)} fullWidth maxWidth="xs">
        <DialogTitle>{measurementForm.id ? 'Editar measurement' : 'Nuevo measurement'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Nombre"
                value={measurementForm.name}
                onChange={(e) => setMeasurementForm({ ...measurementForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Posición"
                value={measurementForm.position}
                onChange={(e) => setMeasurementForm({ ...measurementForm, position: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMeasurementForm(false)}>Cancelar</Button>
          <SaveButton loading={saving} disabled={saving} onClick={saveMeasurement}>Guardar</SaveButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeviceTypeForm} onClose={() => setOpenDeviceTypeForm(false)} fullWidth maxWidth="md">
        <DialogTitle>{deviceTypeForm.id ? 'Editar device type' : 'Nuevo device type'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth required label="Marca" value={deviceTypeForm.brand} onChange={(e) => setDeviceTypeForm({ ...deviceTypeForm, brand: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth required label="Modelo" value={deviceTypeForm.model} onChange={(e) => setDeviceTypeForm({ ...deviceTypeForm, model: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={INTERFACES}
                value={deviceTypeForm.interface || null}
                onChange={(e, value) => setDeviceTypeForm({ ...deviceTypeForm, interface: value || '' })}
                renderInput={(params) => <TextField {...params} required label="Interfaz" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={CATEGORIES}
                value={deviceTypeForm.category || null}
                onChange={(e, value) => setDeviceTypeForm({ ...deviceTypeForm, category: value || '' })}
                renderInput={(params) => <TextField {...params} required label="Categoría" />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Subcategoría" value={deviceTypeForm.subcategory} onChange={(e) => setDeviceTypeForm({ ...deviceTypeForm, subcategory: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Posición" value={deviceTypeForm.position} onChange={(e) => setDeviceTypeForm({ ...deviceTypeForm, position: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={measurementOptions}
                getOptionLabel={(option) => option?.name || ''}
                value={_.filter(measurementOptions, (option) => (deviceTypeForm.measurement_type_ids || []).includes(option.id))}
                onChange={(e, value) => setDeviceTypeForm({ ...deviceTypeForm, measurement_type_ids: _.map(value, 'id') })}
                renderInput={(params) => <TextField {...params} label="Measurement types" />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeviceTypeForm(false)}>Cancelar</Button>
          <SaveButton loading={saving} disabled={saving} onClick={saveDeviceType}>Guardar</SaveButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceTypesIndex;
